// Pivot the PHR's panel-centric labs + vitals into an analyte-by-date table
// (the "labs data table"): each row is a marker, each column a draw date, plus a
// Vitals section at the top. Mirrors the old Dashboard's buildLabsTable() shape,
// adapted to the new PHR model and reusing labViz for grouping + ranges.
import type { PHR, VitalKey, VitalSample } from './types';
import { LAB_GROUPS, categorize } from './labViz';
import { LAB_CATALOG } from './labCatalog';

// Canonical grouping key for an analyte, so the SAME marker recorded under different
// spellings across panels (Hemoglobin/HGB, Platelets/Platelet, RDW-CV/RDWCV) collapses
// into ONE row/series instead of fragmenting (which made values look "missing"). Prefer
// the catalog's matcher (→ its id + display label); else a normalized fallback that
// drops parentheticals/punctuation and a trailing plural "s".
function analyteKey(name: string): { key: string; label?: string; unit?: string } {
  const cat = LAB_CATALOG.find((c) => c.re.test(name));
  if (cat) return { key: 'c:' + cat.id, label: cat.label, unit: cat.unit };
  return { key: 'n:' + name.toLowerCase().replace(/\([^)]*\)/g, ' ').replace(/[^a-z0-9]+/g, '').replace(/s$/, '') };
}

export type Cell = { value: number | string; flag?: string; full: string; srcId?: string; n?: number };
export type LabTableRow = { analyte: string; unit: string; refRange: string; cat: string; editKind: 'lab' | 'vital' | 'none'; field?: string; values: Record<string, Cell> };
export type LabTableSection = { name: string; kind: 'vitals' | 'labs'; source?: string; rows: LabTableRow[] };

// ── Dates ──────────────────────────────────────────────────────────────────────
/** Parse MM/DD/YYYY or YYYY-MM-DD (or anything Date.parse handles) → epoch ms. */
export function parseDate(s: string): number | null {
  if (!s) return null;
  let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})(?:[ T](\d{1,2}):(\d{2}))?/);   // MM/DD/YYYY [HH:MM]
  if (m) { const y = +m[3] < 100 ? 2000 + +m[3] : +m[3]; return new Date(y, +m[1] - 1, +m[2], m[4] ? +m[4] : 0, m[5] ? +m[5] : 0).getTime(); }
  m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?/);            // YYYY-MM-DD [HH:MM]
  if (m) return new Date(+m[1], +m[2] - 1, +m[3], m[4] ? +m[4] : 0, m[5] ? +m[5] : 0).getTime();
  const t = Date.parse(s);
  return Number.isNaN(t) ? null : t;
}
/** Column key for a date — MM/DD/YYYY (groups same calendar day across panels,
 *  but keeps years distinct so columns sort chronologically across years). */
export function colKey(s: string): string {
  const t = parseDate(s);
  if (t == null) return (s || '').slice(0, 10);
  const d = new Date(t);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}
/** Short header label for a column key — M/D/YY. */
export function colLabel(key: string): string {
  const t = parseDate(key);
  if (t == null) return key;
  const d = new Date(t);
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}`;
}
/** Header label including time (for intraday columns) — M/D HH:MM. */
export function colLabelTime(key: string): string {
  const t = parseDate(key);
  if (t == null) return key;
  const d = new Date(t);
  const hm = d.getHours() || d.getMinutes() ? ` ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : '';
  return `${d.getMonth() + 1}/${d.getDate()}${hm}`;
}

// ── Vitals → rows ────────────────────────────────────────────────────────────────
const VITAL_REF: Partial<Record<VitalKey, string>> = {
  temperature: '97–100.4', hr: '60–100', rr: '12–20', spo2: '95–100',   // 97=hypothermia, 100.4=fever
};
const VITAL_ORDER: { keys: VitalKey[]; label: string; unit: string; ref: string; field?: string }[] = [
  { keys: ['temperature'], label: 'Temperature', unit: '°F', ref: VITAL_REF.temperature!, field: 'temperature' },
  { keys: ['hr'], label: 'Heart rate', unit: 'bpm', ref: VITAL_REF.hr!, field: 'heartRate' },
  { keys: ['bpSystolic', 'bpDiastolic'], label: 'Blood pressure', unit: 'mmHg', ref: '' },
  { keys: ['rr'], label: 'Respiratory rate', unit: '/min', ref: VITAL_REF.rr!, field: 'respiration' },
  { keys: ['spo2'], label: 'SpO₂', unit: '%', ref: VITAL_REF.spo2!, field: 'spo2' },
  // Height/weight are shown in a separate Body-measurements panel, not this table.
];

function vitalsSection(phr: PHR): LabTableSection | null {
  const byKey = new Map(phr.vitals.map((v) => [v.key, v]));
  const rows: LabTableRow[] = [];
  let source = '';
  for (const cfg of VITAL_ORDER) {
    if (cfg.keys.length === 2) {
      // Blood pressure — combine systolic/diastolic into one "120/80" row.
      const sys = byKey.get(cfg.keys[0]), dia = byKey.get(cfg.keys[1]);
      if (!sys?.samples.length && !dia?.samples.length) continue;
      const values: Record<string, Cell> = {};
      const dayKeys = new Set([...(sys?.samples ?? []), ...(dia?.samples ?? [])].map((s) => colKey(s.date)));
      const latestOf = (arr: VitalSample[] | undefined, k: string) => {
        let best: VitalSample | null = null, bt = -Infinity;
        for (const x of arr ?? []) { if (colKey(x.date) !== k) continue; const t = parseDate(x.date) ?? 0; if (t >= bt) { bt = t; best = x; } }
        return best;
      };
      for (const k of dayKeys) {
        const s = latestOf(sys?.samples, k), d = latestOf(dia?.samples, k);
        if (!s && !d) continue;
        const n = Math.max(sys?.samples.filter((x) => colKey(x.date) === k).length ?? 0, dia?.samples.filter((x) => colKey(x.date) === k).length ?? 0);
        values[k] = { value: `${s ? Math.round(s.value) : '–'}/${d ? Math.round(d.value) : '–'}`, full: (s ?? d)!.date, n };
      }
      source ||= sys?.samples[0]?.source ?? dia?.samples[0]?.source ?? '';
      rows.push({ analyte: cfg.label, unit: cfg.unit, refRange: cfg.ref, cat: 'electrolyte', editKind: 'none', values });
    } else {
      const v = byKey.get(cfg.keys[0]);
      if (!v?.samples.length) continue;
      // Multiple readings per day → keep the LATEST of the day, track how many.
      const byDay = new Map<string, { s: VitalSample; t: number; n: number }>();
      for (const s of v.samples) {
        const k = colKey(s.date), t = parseDate(s.date) ?? 0;
        const e = byDay.get(k);
        if (!e) byDay.set(k, { s, t, n: 1 });
        else { e.n++; if (t >= e.t) { e.s = s; e.t = t; } }
      }
      const values: Record<string, Cell> = {};
      for (const [k, e] of byDay) values[k] = { value: e.s.value, full: e.s.date, srcId: e.s._id, n: e.n };
      source ||= v.samples[0]?.source ?? '';
      rows.push({ analyte: cfg.label, unit: cfg.unit, refRange: cfg.ref, cat: 'vital', editKind: 'vital', field: cfg.field, values });
    }
  }
  return rows.length ? { name: 'Vitals', kind: 'vitals', source, rows } : null;
}

// ── Labs → rows (analyte across all panels, binned into clinical groups) ─────────
function labRows(phr: PHR): LabTableRow[] {
  const order: string[] = [];
  const by = new Map<string, LabTableRow & { _source?: string }>();
  for (const p of phr.labs || []) {
    for (const r of p.results || []) {
      if (r.value == null || String(r.value).trim() === '') continue;
      const name = r.analyte || '';
      if (!name) continue;
      const { key, label, unit } = analyteKey(name);          // merge naming variants
      let row = by.get(key);
      if (!row) {
        row = { analyte: label || name, unit: unit || r.unit || '', refRange: r.refRange || '', cat: categorize(name), editKind: 'lab', values: {}, _source: p.source };
        by.set(key, row); order.push(key);
      }
      if (!row.unit && r.unit) row.unit = r.unit;
      if (!row.refRange && r.refRange) row.refRange = r.refRange;
      const ck = colKey(p.date);
      if (!row.values[ck]) row.values[ck] = { value: r.value, flag: r.flag, full: p.date, srcId: p._id };   // first panel for that day wins
    }
  }
  return order.map((n) => by.get(n)!);
}

function labSections(phr: PHR): LabTableSection[] {
  const flat = labRows(phr);
  const taken = new Set<string>();
  const sections: LabTableSection[] = [];
  for (const g of LAB_GROUPS) {
    const rows = flat.filter((r) => !taken.has(r.analyte) && g.match.test(r.analyte));
    rows.forEach((r) => taken.add(r.analyte));
    if (rows.length) sections.push({ name: g.name, kind: 'labs', source: (rows[0] as any)._source, rows });
  }
  const rest = flat.filter((r) => !taken.has(r.analyte));
  if (rest.length) sections.push({ name: 'Comprehensive List (Additional)', kind: 'labs', source: (rest[0] as any)._source, rows: rest });
  return sections;
}

/** Vitals as INTRADAY columns — every reading (within `sinceMs`) is its own
 *  column (keyed by full datetime), so a short window shows all values, not just
 *  one per day. Used when the covered range is ≤3 days. */
export function buildVitalsIntraday(phr: PHR, sinceMs: number): { dates: string[]; section: LabTableSection } {
  const byKey = new Map(phr.vitals.map((v) => [v.key, v]));
  const colMs = new Map<string, number>();
  const collect = (s?: { samples: VitalSample[] }) => {
    for (const x of s?.samples ?? []) { const t = parseDate(x.date) ?? -1; if (t >= sinceMs) colMs.set(x.date, t); }
  };
  for (const cfg of VITAL_ORDER) { collect(byKey.get(cfg.keys[0])); if (cfg.keys[1]) collect(byKey.get(cfg.keys[1])); }
  const dates = [...colMs.keys()].sort((a, b) => (colMs.get(b)! - colMs.get(a)!));

  const rows: LabTableRow[] = [];
  let source = '';
  for (const cfg of VITAL_ORDER) {
    if (cfg.keys.length === 2) {
      const sys = byKey.get(cfg.keys[0]), dia = byKey.get(cfg.keys[1]);
      const values: Record<string, Cell> = {};
      for (const dt of dates) {
        const s = sys?.samples.find((x) => x.date === dt), d = dia?.samples.find((x) => x.date === dt);
        if (!s && !d) continue;
        values[dt] = { value: `${s ? Math.round(s.value) : '–'}/${d ? Math.round(d.value) : '–'}`, full: dt };
      }
      if (Object.keys(values).length) { source ||= sys?.samples[0]?.source ?? ''; rows.push({ analyte: cfg.label, unit: cfg.unit, refRange: cfg.ref, cat: 'electrolyte', editKind: 'none', values }); }
    } else {
      const v = byKey.get(cfg.keys[0]);
      const values: Record<string, Cell> = {};
      for (const s of v?.samples ?? []) { const t = parseDate(s.date) ?? -1; if (t < sinceMs) continue; values[s.date] = { value: s.value, full: s.date, srcId: s._id }; }
      if (Object.keys(values).length) { source ||= v?.samples[0]?.source ?? ''; rows.push({ analyte: cfg.label, unit: cfg.unit, refRange: cfg.ref, cat: 'vital', editKind: 'vital', field: cfg.field, values }); }
    }
  }
  return { dates, section: { name: 'Vitals', kind: 'vitals', source, rows } };
}

export function buildLabTable(phr: PHR): LabTableSection[] {
  const out: LabTableSection[] = [];
  const v = vitalsSection(phr);
  if (v) out.push(v);
  out.push(...labSections(phr));
  return out;
}

// ── Medication regimens (Gantt windows) ─────────────────────────────────────────
export type Regimen = { name: string; detail?: string; indication?: string; source?: string; start: number; stop: number | null };

/** On-brand colour cycle for regimen bars (tokens + a couple of muted tones). */
export const REGIMEN_COLORS = ['var(--accent)', 'var(--info)', 'var(--ok)', 'var(--warn)', 'var(--danger)', '#9C8BB0', '#6F9AA6'];

/** Medications that have a start date, as start→stop windows (oldest first). */
export function buildRegimens(phr: PHR): Regimen[] {
  const out: Regimen[] = [];
  for (const m of phr.medications || []) {
    const start = parseDate(m.since || '');
    if (start == null) continue;   // need a start to place it on the timeline
    const stop = m.until ? parseDate(m.until) : null;
    const detail = [m.dose, m.route, m.frequency].filter(Boolean).join(' · ');
    out.push({ name: m.name, detail, indication: m.indication, source: m.source, start, stop });
  }
  out.sort((a, b) => a.start - b.start);
  return out;
}

// ── Regimen lanes: ONE row per medication, multiple courses merged into segments ──
export type RegimenMed = { id?: string; since?: string; until?: string };
export type RegimenSeg = { start: number; stop: number | null; meds: RegimenMed[] };
export type RegimenPattern = 'solid' | 'hatch' | 'cross';
export type RegimenLane = { name: string; detail?: string; color: string; pattern: RegimenPattern; segments: RegimenSeg[] };

/** Group every medication course by drug name and merge overlapping/adjacent
 *  courses into segments, so each drug appears once with its real on/off windows.
 *  Each segment keeps the source course docs so the graph can edit start/stop. */
export function buildRegimenLanes(phr: PHR): RegimenLane[] {
  const byName = new Map<string, { start: number; stop: number; med: RegimenMed }[]>();   // stop=Infinity = ongoing
  for (const m of phr.medications || []) {
    const start = parseDate(m.since || '');
    if (start == null) continue;
    const stop = m.until ? (parseDate(m.until) ?? Infinity) : Infinity;
    if (!byName.has(m.name)) byName.set(m.name, []);
    byName.get(m.name)!.push({ start, stop, med: { id: m._id, since: m.since, until: m.until } });
  }
  const lanes: RegimenLane[] = [];
  for (const [name, ivs] of byName) {
    ivs.sort((a, b) => a.start - b.start);
    const merged: { start: number; stop: number; meds: RegimenMed[] }[] = [];
    for (const iv of ivs) {
      const last = merged[merged.length - 1];
      if (last && iv.start <= last.stop) { last.stop = Math.max(last.stop, iv.stop); last.meds.push(iv.med); }
      else merged.push({ start: iv.start, stop: iv.stop, meds: [iv.med] });
    }
    const segments: RegimenSeg[] = merged.map((s) => ({ start: s.start, stop: s.stop === Infinity ? null : s.stop, meds: s.meds }));
    lanes.push({ name, detail: `${ivs.length} course${ivs.length > 1 ? 's' : ''}`, color: '', pattern: 'solid', segments });
  }

  // Order: current (active) meds first — most-recently-started on top — then
  // inactive meds ranked by how heavily they were dosed (course count, then total
  // days on the drug, then recency) so the frequently-used drugs surface first.
  const last = (l: RegimenLane) => l.segments[l.segments.length - 1];
  const anchor = lanes.length ? Math.max(...lanes.map((l) => { const s = last(l); return s.stop ?? s.start; })) : 0;
  const isActive = (l: RegimenLane) => { const s = last(l); return s.stop == null || s.stop >= anchor; };
  const courses = (l: RegimenLane) => l.segments.reduce((n, s) => n + s.meds.length, 0);
  const totalDays = (l: RegimenLane) => l.segments.reduce((n, s) => n + ((s.stop ?? anchor) - s.start), 0);
  const startLast = (l: RegimenLane) => last(l).start;
  lanes.sort((a, b) => {
    const aa = isActive(a), ba = isActive(b);
    if (aa !== ba) return aa ? -1 : 1;
    if (aa) return startLast(b) - startLast(a);
    return courses(b) - courses(a) || totalDays(b) - totalDays(a) || startLast(b) - startLast(a);
  });
  // Cycle colours; once colours wrap, change the fill pattern (solid → hatch →
  // cross) so two drugs never share the same colour *and* texture.
  const PATS: RegimenPattern[] = ['solid', 'hatch', 'cross'];
  const nc = REGIMEN_COLORS.length;
  lanes.forEach((l, i) => { l.color = REGIMEN_COLORS[i % nc]; l.pattern = PATS[Math.floor(i / nc) % PATS.length]; });
  return lanes;
}

/** Collapse points to one per calendar day (mean) — for a cleaner long-range plot. */
export function dailyMean(pts: { t: number; y: number }[]): { t: number; y: number }[] {
  const m = new Map<number, { s: number; n: number }>();
  for (const p of pts) {
    if (!Number.isFinite(p.t) || !Number.isFinite(p.y)) continue;
    const d = new Date(p.t); const k = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const e = m.get(k); if (!e) m.set(k, { s: p.y, n: 1 }); else { e.s += p.y; e.n++; }
  }
  return [...m.entries()].map(([t, e]) => ({ t, y: Math.round((e.s / e.n) * 10) / 10 })).sort((a, b) => a.t - b.t);
}

/** Sorted (newest-first) MM/DD columns that actually carry a value in `sections`. */
export function datesIn(sections: LabTableSection[]): string[] {
  const set = new Set<string>();
  for (const s of sections) for (const r of s.rows) for (const k of Object.keys(r.values)) {
    const c = r.values[k];
    if (c && c.value !== '' && c.value != null) set.add(k);
  }
  return [...set].sort((a, b) => (parseDate(b) ?? 0) - (parseDate(a) ?? 0));
}
